import { useEffect, useMemo, useRef, useState } from 'react';
import { PlusSvg } from '../../../../../command_center/custom_components/Svgs';
import './FilterGroupsHeader.scss';
import {
  convertFiltersToApiFormat,
  createLookupMap,
  createMetaData,
  enableNextTabAccordingly,
  getActiveGroupsLength,
  updateFilterGroup,
} from './Utils';
import { showToastMessage } from '../../../../../../utils/commonFunctions';
import useUrlParams from '../../../../hooks/useUrlParams';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import { ConsoleFilterOptionType } from '../../../../types';
import useSocket from '../../../../hooks/useSocket';
import {
  CrossSvg,
  DeleteIcon,
  RefreshFilterButton,
  SaveFileSvg,
  SearchSvg,
} from '../../../../../CommonComponents/svgs/Svgs';
import FilterGroupInput from './FilterGroupInput';
import useFilters from '../../../../hooks/useFilters';

//// ----------------------------
//// Component: FilterGroupsHeader
//// ----------------------------

const FilterGroupsHeader = ({ setAllFilters, allFilters, handleChangeFilter }) => {
  //// ----------------------------
  //// Local state
  //// ----------------------------
  const [isFilterGroupModalOpen, setIsFilterGroupModalOpen] = useState(false);
  const [activeFilterGroupId, setActiveFilterGroupId] = useState<string | null>(null);
  const [renamingTabId, setRenamingTabId] = useState<string | null>(null);
  const [refreshButtonActive, setRefreshButtonActive] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [hideInput, setHideInput] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupListModalStyle, setGroupListModalStyle] = useState({
    position: 'absolute',
    top: 0,
    left: 0,
  });

  //// ----------------------------
  //// Refs
  //// ----------------------------
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const hasRun = useRef<boolean>(false);

  //// ----------------------------
  //// Context / Hooks
  //// ----------------------------
  const { scenario_id } = useUrlParams();
  const { socket } = useSocket();
  const {
    consoleFilterGroups,
    setConsoleFilterGroups,
    consoleFilterOptions,
    setOriginalFilterMetadata,
    patchFilterGroup,
    originalFilterMetadata,
    deleteFilterGroup,
    getFilterGroups,
    referenceIdConsole,
  } = useFilters();

  //// ----------------------------
  //// Effects
  //// ----------------------------

  // Initialize or set first active group when groups become available
  useEffect(() => {
    if (hasRun.current) return;
    runFilters(false, consoleFilterGroups);
  }, [consoleFilterGroups]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        modalRef.current &&
        !modalRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsFilterGroupModalOpen(false);
      }
    };

    if (isFilterGroupModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterGroupModalOpen]);

  // Listen for websocket messages about filter group updates from collaborators
  useEffect(() => {
    if (!scenario_id || !socket || !referenceIdConsole) return;

    const handleMessage = (event: any) => {
      try {
        const data = JSON.parse(event.data);
        const parsedMessage = JSON.parse(data.message);

        if (
          parsedMessage?.eventType === 'FILTER_GROUPS_UPDATED' &&
          parsedMessage?.requestId &&
          referenceIdConsole !== parsedMessage.requestId
        ) {
          showToastMessage('info', 'A collaborator has updated Filters');
          setRefreshButtonActive(true);
        }
      } catch (error) {
        console.error('Error parsing websocket message:', error);
      }
    };

    socket.addEventListener('message', handleMessage);
    return () => socket.removeEventListener('message', handleMessage);
  }, [socket, scenario_id, referenceIdConsole]);

  // Scroll active tab into view
  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const activeTab = container.querySelector('.filter_tab.active') as HTMLElement;
    if (!activeTab) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    const isVisible = tabRect.left >= containerRect.left && tabRect.right <= containerRect.right;

    if (!isVisible) {
      activeTab.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
  }, [activeFilterGroupId]);

  //// ----------------------------
  //// API / Actions
  //// ----------------------------

  // Refresh filter groups from server
  const handleRefreshFilterGroups = async () => {
    setRefreshLoading(true);
    const latestGroups = await getFilterGroups(scenario_id);
    if (latestGroups) {
      runFilters(true, latestGroups);
    }
    setRefreshButtonActive(false);
  };

  // Delete a filter group and enable next appropriate tab
  const handleDeleteFilterGroup = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteFilterGroup(id);
    enableNextTabAccordingly(
      e,
      id,
      consoleFilterGroups,
      setActiveFilterGroupId,
      setAllFilters,
      setOriginalFilterMetadata,
      handleChangeFilter,
      activeFilterTab,
      setConsoleFilterGroups
    );
  };

  // Patch (save) current active group's filters to server
  const handlePatchFilterGroup = () => {
    if (!activeFilterGroupId || !consoleFilterGroups) return;

    const activeGroup = consoleFilterGroups.get(activeFilterGroupId);
    if (!activeGroup) return;

    const filterMetadata = convertFiltersToApiFormat(allFilters);
    const requestPayload = { ...activeGroup, FilterMetadata: filterMetadata };

    const onSuccess = () => {
      setConsoleFilterGroups((prev) => {
        const newMap = new Map(prev);
        const currentActiveGroup = newMap.get(activeFilterGroupId || '');
        if (currentActiveGroup) {
          newMap.set(activeFilterGroupId || '', { ...currentActiveGroup, ...requestPayload });
        }
        return newMap;
      });

      setOriginalFilterMetadata(allFilters);
    };

    patchFilterGroup('update', requestPayload, onSuccess);
  };

  //// ----------------------------
  //// UI helpers
  //// ----------------------------

  // Position and open create modal intelligently based on available space
  const handleOpenCreateModal = () => {
    if (isFilterGroupModalOpen) {
      setSearchQuery('');
      return setIsFilterGroupModalOpen(false);
    }

    const button = buttonRef.current;
    const panel = document.querySelector('.filter_header_body');
    if (!button || !panel) return;

    const buttonRect = button.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const spaceRight = panelRect.right - buttonRect.left;
    const left = spaceRight < 300 ? -200 : 4;

    setGroupListModalStyle({ position: 'absolute', top: 40, left });
    setIsFilterGroupModalOpen(true);
  };

  // Compute whether filters changed vs original
  const isFilterChanged = useMemo(() => {
    const current = JSON.stringify(allFilters);
    const original = JSON.stringify(originalFilterMetadata);
    return current !== original;
  }, [allFilters, originalFilterMetadata]);

  // Handle selecting a filter group from the list and set it active
  const handleFilterGroupListItemClick = (id: string, filterData: any) => {
    updateFilterGroup(id, { isHeaderActive: true }, setConsoleFilterGroups);
    activeFilterTab(id, filterData);
    setIsFilterGroupModalOpen(false);
  };

  // Show input for adding a new filter group
  const handleAddNewButtonClick = () => {
    setHideInput(false);
    setIsFilterGroupModalOpen(false);
    updateFilterGroup(activeFilterGroupId ?? '', { isActive: false }, setConsoleFilterGroups);
    setActiveFilterGroupId(null);
  };

  // Make a given tab active and create metadata for its filters
  const activeFilterTab = (id: string, filterData: any) => {
    if (activeFilterGroupId === id) return;

    if (activeFilterGroupId) {
      updateFilterGroup(activeFilterGroupId, { isActive: false }, setConsoleFilterGroups);
    }

    updateFilterGroup(id, { isActive: true }, setConsoleFilterGroups);
    setActiveFilterGroupId(id);

    // Create metadata and update parent state accordingly
    createMetaData(
      filterData,
      false,
      setOriginalFilterMetadata,
      handleChangeFilter,
      setAllFilters,
      setRefreshLoading,
      sourceMap,
      destinationMap
    );
  };

  //// ----------------------------
  //// Lookup maps
  //// ----------------------------
  const sourceMap = useMemo(
    () => createLookupMap(consoleFilterOptions?.SaIdFilter),
    [consoleFilterOptions?.SaIdFilter]
  );

  const destinationMap = useMemo(
    () => createLookupMap(consoleFilterOptions?.DaIdFilter),
    [consoleFilterOptions?.DaIdFilter]
  );

  // Remove a filter tab and enable next appropriate tab
  const removeFilterTab = (id: string, e: React.MouseEvent) => {
    enableNextTabAccordingly(
      e,
      id,
      consoleFilterGroups,
      setActiveFilterGroupId,
      setAllFilters,
      setOriginalFilterMetadata,
      handleChangeFilter,
      activeFilterTab,
      setConsoleFilterGroups
    );

    updateFilterGroup(id, { isActive: false, isHeaderActive: false }, setConsoleFilterGroups);
  };

  // Double click to rename a tab
  const handleTabDoubleClick = (id: string) => {
    const group = consoleFilterGroups.get(id);
    if (!group) return;

    setRenamingTabId(id);
    setInputValue(group.filterGroupName);
    // Optionally focus input after render
    // setTimeout(() => inputRef.current?.focus(), 0);
  };

  //// ----------------------------
  //// Initialization
  //// ----------------------------
  const runFilters = (
    isRefresh: boolean,
    filtergroups: Map<string, ConsoleFilterOptionType>
  ) => {
    if (!filtergroups || filtergroups.size === 0) return;
    hasRun.current = true;

    const groups = Array.from(filtergroups.values());
    const targetActiveGroup = groups.find((g) => g?.isActive) ?? groups.find((g) => g?.isHeaderActive);
    if (!targetActiveGroup) return;

    // Ensure only one group is active
    setConsoleFilterGroups(prev => {
      const newMap = new Map(prev);
      newMap.forEach((group, id) => {
        newMap.set(id, {
          ...group,
          isActive: id === targetActiveGroup.filterGroupId
        });
      });

      return newMap;
    });
    setActiveFilterGroupId(targetActiveGroup?.filterGroupId)

    if (isRefresh) {
      createMetaData(
        targetActiveGroup.FilterMetadata,
        isRefresh,
        setOriginalFilterMetadata,
        handleChangeFilter,
        setAllFilters,
        setRefreshLoading,
        sourceMap,
        destinationMap
      );
    } else {
      activeFilterTab(targetActiveGroup.filterGroupId, targetActiveGroup.FilterMetadata);
    }
  };

  //// ----------------------------
  //// Render
  //// ----------------------------
  return (
    <div className="filter_header_body">
      <div
        className="filter_header_body_left_section"
        style={{
          maxWidth:
            !refreshButtonActive && !isFilterChanged
              ? '100%'
              : !refreshButtonActive || !isFilterChanged
                ? 'calc(100% - 31px)'
                : 'calc(100% - 60px)',
        }}
      >
        <div
          ref={tabsContainerRef}
          className="filter_tabs_container modal__scrollbar__white__horizontal"
          style={{ maxWidth: '100%' }}
        >
          {Array.from<ConsoleFilterOptionType>(consoleFilterGroups.values())
            .filter((group) => group?.isHeaderActive)
            .map((group) =>
              renamingTabId === group.filterGroupId ? (
                <FilterGroupInput
                  key={group.filterGroupId}
                  id={group?.filterGroupId}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  setRenamingTabId={setRenamingTabId}
                  setHideInput={setHideInput}
                  activeFilterTab={activeFilterTab}
                />
              ) : (
                <CustomToolTip title={group?.filterGroupName} placement="top">
                  <div
                    role="button"
                    key={group?.filterGroupId}
                    className={`filter_tab ${group?.filterGroupId === activeFilterGroupId ? 'active' : ''}`}
                    onClick={() => {
                      activeFilterTab(group?.filterGroupId, group?.FilterMetadata)
                    }}
                    onDoubleClick={() => handleTabDoubleClick(group?.filterGroupId)}
                  >
                    <span>{group?.filterGroupName}</span>

                    {group?.isActive && getActiveGroupsLength(consoleFilterGroups) > 1 && (
                      <button className="remove_filter_tab_icon" onClick={(e) => removeFilterTab(group?.filterGroupId, e)}>
                        <CrossSvg />
                      </button>
                    )}
                  </div>
                </CustomToolTip>
              )
            )}
        </div>

        {/* Create new input box */}
        {!hideInput && (
          <FilterGroupInput
            id={null}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setRenamingTabId={setRenamingTabId}
            setHideInput={setHideInput}
            activeFilterTab={activeFilterTab}
          />
        )}

        <div className="add_filtergroup_button_wrapper" ref={buttonRef}>
          <CustomToolTip title="Filter Groups" placement="top">
            <button className={`filter_group_add_icon_container ${isFilterGroupModalOpen ? 'active' : ''}`} onClick={handleOpenCreateModal}>
              <PlusSvg />
            </button>
          </CustomToolTip>

          {isFilterGroupModalOpen && (
            <div ref={modalRef} className="filtergroups_create_modal" style={{ top: groupListModalStyle?.top, left: groupListModalStyle?.left }}>
              <div className="filtergroups_search_wrapper">
                <SearchSvg />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="filtergroups_search_input"
                />
              </div>
              <div className="filtergroups_list_body modal__scrollbar__white">
                {Array.from<ConsoleFilterOptionType>(consoleFilterGroups.values())
                  .filter((group) => group?.filterGroupName?.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((group) => (
                  <div className="filtergroups_list_row" key={group?.filterGroupId} onClick={() => handleFilterGroupListItemClick(group.filterGroupId, group?.FilterMetadata)}>
                    <CustomToolTip title={group?.filterGroupName} placement="left">
                      <span>{group?.filterGroupName}</span>
                    </CustomToolTip>

                    {consoleFilterGroups?.size > 1 && (
                      <div className="d-flex cursor-pointer" onClick={(e) => handleDeleteFilterGroup(group.filterGroupId, e)}>
                        <DeleteIcon />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Add new filter group row */}
              <div role="button" className="add_new_filtergroup_row cursor-pointer" onClick={handleAddNewButtonClick}>
                <PlusSvg />
                <span>Add New</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save and refresh button section */}
      <div className="filter_header_body_right_section">
        {refreshButtonActive && (
          <CustomToolTip title="Refresh" placement="top">
            <button className={`save_filter_group_button active`} onClick={handleRefreshFilterGroups}>
              <RefreshFilterButton loading={refreshLoading} />
            </button>
          </CustomToolTip>
        )}

        {isFilterChanged && (
          <CustomToolTip title="Save" placement="top">
            <button className={`save_filter_group_button active`} onClick={handlePatchFilterGroup}>
              <SaveFileSvg />
            </button>
          </CustomToolTip>
        )}
      </div>
    </div>
  );
};

export default FilterGroupsHeader;